import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { MessageSquare, Send, Trash2, Reply, ThumbsUp, Heart, PartyPopper, Handshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SocialFeed({ competitionId, competitionName, onPostsCountChange }) {
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, [competitionId]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(
        `${API}/competitions/${competitionId}/social-posts`,
        { withCredentials: true }
      );
      const postsData = response.data.posts || [];
      setPosts(postsData);
      
      // Notify parent component of posts count
      if (onPostsCountChange) {
        onPostsCountChange(postsData.length);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load social feed');
      }
    } finally {
      setLoading(false);
    }
  };

  const createPost = async (content, parentPostId = null) => {
    if (!content.trim()) {
      toast.error('Please enter some content');
      return;
    }

    if (content.length > 500) {
      toast.error('Post too long (max 500 characters)');
      return;
    }

    setPosting(true);
    try {
      await axios.post(
        `${API}/competitions/${competitionId}/social-posts`,
        { content: content.trim(), parent_post_id: parentPostId },
        { withCredentials: true }
      );
      
      // Clear inputs
      setNewPostContent('');
      setReplyContent('');
      setReplyingTo(null);
      
      // Refresh posts
      await fetchPosts();
      toast.success(parentPostId ? 'Reply posted!' : 'Post created!');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post and all its replies?')) return;

    try {
      await axios.delete(
        `${API}/competitions/${competitionId}/social-posts/${postId}`,
        { withCredentials: true }
      );
      await fetchPosts();
      toast.success('Post deleted');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleReaction = async (postId, reactionType) => {
    try {
      const response = await axios.post(
        `${API}/competitions/${competitionId}/social-posts/${postId}/react`,
        { reaction: reactionType },
        { withCredentials: true }
      );
      
      // Update local state
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return { ...post, reactions: response.data.reactions };
        }
        // Also check replies
        if (post.replies) {
          post.replies = post.replies.map(reply => 
            reply.id === postId ? { ...reply, reactions: response.data.reactions } : reply
          );
        }
        return post;
      }));
    } catch (error) {
      console.error('Error reacting to post:', error);
      toast.error('Failed to add reaction');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getReactionIcon = (type) => {
    const icons = {
      like: <ThumbsUp className="w-4 h-4" />,
      love: <Heart className="w-4 h-4" />,
      celebrate: <PartyPopper className="w-4 h-4" />,
      clap: <Handshake className="w-4 h-4" />
    };
    return icons[type] || null;
  };

  const getReactionEmoji = (type) => {
    const emojis = {
      like: '👍',
      love: '❤️',
      celebrate: '🎉',
      clap: '👏'
    };
    return emojis[type] || '';
  };

  const renderReactions = (reactions, postId) => {
    if (!reactions || Object.keys(reactions).length === 0) return null;

    return (
      <div className="flex flex-wrap gap-2 mt-3">
        {Object.entries(reactions).map(([type, users]) => {
          if (!users || users.length === 0) return null;
          const userReacted = users.includes(user.id);
          
          return (
            <button
              key={type}
              onClick={() => handleReaction(postId, type)}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition ${
                userReacted 
                  ? 'bg-accent-tint text-ink border border-accent' 
                  : 'bg-surface-2 text-ink-muted border border-line hover:bg-line'
              }`}
            >
              <span>{getReactionEmoji(type)}</span>
              <span>{users.length}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderReactionButtons = (postId) => {
    return (
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => handleReaction(postId, 'like')}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-ink-muted hover:bg-surface-2 transition"
          title="Like"
        >
          👍 Like
        </button>
        <button
          onClick={() => handleReaction(postId, 'love')}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-ink-muted hover:bg-surface-2 transition"
          title="Love"
        >
          ❤️ Love
        </button>
        <button
          onClick={() => handleReaction(postId, 'celebrate')}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-ink-muted hover:bg-surface-2 transition"
          title="Celebrate"
        >
          🎉 Celebrate
        </button>
        <button
          onClick={() => handleReaction(postId, 'clap')}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-ink-muted hover:bg-surface-2 transition"
          title="Applause"
        >
          👏 Clap
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-6 h-6 text-accent" />
        <h3 className="text-xl font-bold text-ink">Competition Chat</h3>
      </div>

      {/* New Post Form */}
      <div className="bg-white rounded-lg border border-line p-4 shadow-sm">
        <textarea
          value={newPostContent}
          onChange={(e) => setNewPostContent(e.target.value)}
          placeholder="Share something with the group..."
          maxLength={500}
          rows={3}
          className="w-full px-3 py-2 border border-line-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-accent resize-none"
        />
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-ink-soft">
            {newPostContent.length}/500 characters
          </span>
          <Button
            onClick={() => createPost(newPostContent)}
            disabled={posting || !newPostContent.trim()}
            className="bg-ink hover:bg-ink-hover"
            size="sm"
          >
            <Send className="w-4 h-4 mr-1" />
            Post
          </Button>
        </div>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4">
        {posts.length === 0 ? (
          <div className="text-center py-12 text-ink-soft">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-line-strong" />
            <p>No posts yet. Be the first to share!</p>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="bg-white rounded-lg border border-line p-4 shadow-sm">
              {/* Post Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <img
                    src={post.user_picture || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect fill='%237c3aed' width='40' height='40'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='white' font-size='20' font-family='Arial'%3E%3F%3C/text%3E%3C/svg%3E"}
                    alt={post.user_name}
                    className="w-10 h-10 rounded-full border border-accent"
                  />
                  <div>
                    <p className="font-semibold text-ink">{post.user_name}</p>
                    <p className="text-xs text-ink-soft">{formatDate(post.created_at)}</p>
                  </div>
                </div>
                {post.user_id === user.id && (
                  <button
                    onClick={() => deletePost(post.id)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Post Content */}
              <p className="text-ink-muted whitespace-pre-wrap mb-3">{post.content}</p>

              {/* Reactions Display */}
              {renderReactions(post.reactions, post.id)}

              {/* Reaction Buttons */}
              {renderReactionButtons(post.id)}

              {/* Reply Button */}
              <button
                onClick={() => setReplyingTo(replyingTo === post.id ? null : post.id)}
                className="text-sm text-accent hover:text-ink font-medium flex items-center gap-1 mt-3"
              >
                <Reply className="w-4 h-4" />
                Reply
              </button>

              {/* Reply Form */}
              {replyingTo === post.id && (
                <div className="mt-3 ml-6 border-l-2 border-line pl-4">
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Write a reply..."
                    maxLength={500}
                    rows={2}
                    className="w-full px-3 py-2 border border-line-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-accent resize-none text-sm"
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <Button
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyContent('');
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => createPost(replyContent, post.id)}
                      disabled={posting || !replyContent.trim()}
                      className="bg-ink hover:bg-ink-hover"
                      size="sm"
                    >
                      Reply
                    </Button>
                  </div>
                </div>
              )}

              {/* Replies */}
              {post.replies && post.replies.length > 0 && (
                <div className="mt-4 ml-6 space-y-3 border-l-2 border-line pl-4">
                  {post.replies.map((reply) => (
                    <div key={reply.id} className="bg-app rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <img
                            src={reply.user_picture || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Crect fill='%237c3aed' width='32' height='32'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='white' font-size='16' font-family='Arial'%3E%3F%3C/text%3E%3C/svg%3E"}
                            alt={reply.user_name}
                            className="w-8 h-8 rounded-full border border-accent"
                          />
                          <div>
                            <p className="font-semibold text-sm text-ink">{reply.user_name}</p>
                            <p className="text-xs text-ink-soft">{formatDate(reply.created_at)}</p>
                          </div>
                        </div>
                        {reply.user_id === user.id && (
                          <button
                            onClick={() => deletePost(reply.id)}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-ink-muted whitespace-pre-wrap">{reply.content}</p>
                      
                      {/* Reply Reactions */}
                      {renderReactions(reply.reactions, reply.id)}
                      {renderReactionButtons(reply.id)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
